/**
 * Theme Configuration
 * 
 * This theme file is structured to support easy light/dark theme switching.
 * Colors are organized by usage frequency and semantic meaning for better maintainability.
 * 
 * Best Practices:
 * - Use theme-aware colors: `theme.colors.light.*` or `theme.colors.dark.*` for theme-dependent values
 * - Use base palettes (gray, primary, status) for theme-agnostic colors
 * - Use semantic colors (status, interactive) for consistent UI patterns
 * - Use rgba overlays for transparency effects
 * 
 * Usage Examples:
 * ```tsx
 * // Theme-aware (changes with light/dark mode)
 * background: ${props => props.theme.colors.light.background.primary};
 * 
 * // Theme-agnostic (always the same)
 * color: ${props => props.theme.colors.primary.accent};
 * 
 * // With helper function
 * const colors = getThemeColors('light');
 * background: colors.background.primary;
 * ```
 */

export const theme = {
  colors: {
    // ============================================
    // MOST FREQUENTLY USED COLORS (Top Priority)
    // ============================================
    
    // Primary brand colors (most used throughout app)
    primary: {
      accent: '#FB6F2A',           // Primary orange accent - MOST USED
      accentLight: '#FF9A66',      // Lighter orange variant
      accentDark: '#D33324',       // Dark red-orange - VERY COMMON
      gradient: 'linear-gradient(135deg, #FB6F2A, #FF9A66)',
      // Blue scale (for primary palette completeness)
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
    },

    // Gray scale - most commonly used neutral colors
    gray: {
      // Standard scale
      25: '#fafafa',
      50: '#f5f5f5',      // Main background - VERY COMMON
      100: '#f3f4f6',     // Subtle background - COMMON
      200: '#e5e7eb',     // Borders, dividers - VERY COMMON
      300: '#D2D5DA',     // Light borders
      400: '#9ca3af',     // Tertiary text - COMMON
      500: '#6b7280',     // Secondary text - COMMON
      550: '#6E6E6E',     // Neutral gray - VERY COMMON
      600: '#4b5563',
      700: '#374151',     // Dark text on light bg - COMMON
      800: '#2D2D2D',
      900: '#101018',     // Primary text - COMMON
      // Additional commonly used variants
      neutral: '#6E6E6E',  // Neutral gray - VERY COMMON
      darkText: '#101018', // Dark text variant - VERY COMMON
      muted: '#666666',    // Muted text variant - COMMON
    },

    // Border colors (most frequently used)
    border: {
      primary: '#D9D9DC',   // Primary border - MOST USED
      secondary: '#e5e7eb',  // Secondary border - COMMON
      tertiary: '#d1d5db',  // Tertiary border - COMMON
      light: '#D2D5DA',     // Light border variant
    },

    // Absolute colors
    white: '#ffffff',        // White - VERY COMMON
    black: '#000000',        // Black

    // ============================================
    // SEMANTIC STATUS COLORS
    // ============================================
    status: {
      // Success colors
      success: '#10b981',
      successAlt: '#14AE5C',   // Alternative success green - COMMON
      successLight: '#059669',
      successBg: '#D3FFE7',    // Success background
      successBgLight: '#EFFFF6', // Light success background
      
      // Error colors
      error: '#ef4444',
      errorAlt: '#EC221F',     // Alternative error red - COMMON
      errorDark: '#DC2626',    // Darker error red - COMMON
      errorDarker: '#D92D20',  // Even darker error - COMMON
      errorDarkest: '#B91C1C', // Darkest error - COMMON
      errorText: '#991b1b',    // Error text color - COMMON
      errorBg: '#fee2e2',      // Error background - COMMON
      errorBgLight: '#fecaca',  // Light error background - COMMON
      
      // Warning colors
      warning: '#f59e0b',
      warningBg: '#fef3c7',    // Warning background
      warningBgLight: '#fde68a', // Light warning background
      
      // Info colors
      info: '#2563EB',          // Blue info color - COMMON
      infoButton: '#0088FF',     // Info button color - COMMON
      infoButtonHover: '#0077CC', // Info button hover - COMMON
      infoBg: '#EBF8FF',        // Info background - COMMON
      infoBgLight: '#DBEAFE',   // Light info background - COMMON
      
      // Debug
      debug: '#7c3aed',
    },

    // ============================================
    // BUTTON COLORS
    // ============================================
    button: {
      // Secondary button colors
      secondary: '#6c757d',      // Secondary button - COMMON
      secondaryHover: '#5a6268', // Secondary hover - COMMON
      // Info button (already in status, but grouped here for clarity)
      info: '#0088FF',
      infoHover: '#0077CC',
      // Danger button
      danger: '#EC221F',
      dangerHover: '#D33324',
    },

    // ============================================
    // RGBA OVERLAYS & TRANSPARENCY
    // ============================================
    overlay: {
      // Modal overlays
      modal: 'rgba(0, 0, 0, 0.5)',        // Modal backdrop - COMMON
      modalDark: 'rgba(0, 0, 0, 0.8)',     // Dark modal backdrop
      // Focus states
      focus: 'rgba(251, 111, 42, 0.1)',    // Focus ring - COMMON
      focusDark: 'rgba(251, 111, 42, 0.2)', // Dark focus ring
      focusInfo: 'rgba(37, 99, 235, 0.1)', // Info focus ring - COMMON
      focusError: 'rgba(220, 38, 38, 0.1)', // Error focus ring - COMMON
      // Hover states
      hover: 'rgba(255, 255, 255, 0.1)',   // White hover overlay - COMMON
      hoverDark: 'rgba(0, 0, 0, 0.05)',    // Dark hover overlay - COMMON
      hoverDarker: 'rgba(0, 0, 0, 0.1)',   // Darker hover overlay - COMMON
      // Selected states
      selected: 'rgba(74, 144, 226, 0.2)',  // Selected background - COMMON
      selectedHover: 'rgba(255, 255, 255, 0.3)', // Selected hover - COMMON
      // Primary accent overlays
      accent: 'rgba(251, 111, 42, 0.1)',   // Accent overlay
      accentHover: 'rgba(211, 51, 36, 0.4)', // Accent hover shadow - COMMON
      // White overlays
      white10: 'rgba(255, 255, 255, 0.1)', // 10% white
      white20: 'rgba(255, 255, 255, 0.2)', // 20% white
      white30: 'rgba(255, 255, 255, 0.3)', // 30% white
      // Black overlays
      black05: 'rgba(0, 0, 0, 0.05)',      // 5% black
      black10: 'rgba(0, 0, 0, 0.1)',      // 10% black
    },

    // ============================================
    // SPECIAL PURPOSE COLORS
    // ============================================
    special: {
      avatarBg: '#3B4296',        // Avatar default background - COMMON
      orangeTint: '#FFF7ED',       // Orange tint background
      yellowBorder: '#FDE68A',     // Yellow border
      navbarBg: '#232323',        // Navbar background (rgb(35,35,35) as hex) - COMMON
      sidebarBg: '#1a1a1a',        // Sidebar background - COMMON
      sidebarBgAlt: '#232323',    // Alternative sidebar background
    },

    // ============================================
    // CHART COLORS
    // ============================================
    chart: {
      yellow: '#FFCC00',
      blue: '#0ea5e9',
      green: '#10b981',
      red: '#ef4444',
      orange: '#FB6F2A',
      purple: '#8b5cf6',
      cyan: '#06b6d4',
      lime: '#84cc16',
      pink: '#EC4899',
      brown: '#A86534',
    },

    // ============================================
    // GRADIENTS
    // ============================================
    gradients: {
      primary: 'linear-gradient(270deg, #D33324 0%, #FB6F2A 100%)',
      primaryAlt: 'linear-gradient(135deg, #D33324 0%, #FB6F2A 100%)',
      primaryHover: 'linear-gradient(270deg, rgba(251, 111, 42, 0.45) 0%, rgba(255, 154, 102, 0.45) 100%)',
      greenLight: 'linear-gradient(201.18deg, #D3FFE7 3.14%, #EFFFF6 86.04%)',
      redLight: 'linear-gradient(201.18deg, #FFD2D2 3.14%, #FFEFEF 86.04%)',
      warning: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    },

    // ============================================
    // LIGHT THEME COLORS (Default)
    // Organized for easy theme switching
    // ============================================
    light: {
      // Backgrounds
      background: {
        primary: '#f5f5f5',      // Main page background - MOST USED
        secondary: '#ffffff',    // Card/surface background - VERY COMMON
        tertiary: '#f3f4f6',    // Subtle background - COMMON
        hover: '#f9fafb',        // Hover state background - COMMON
        selected: '#EBF8FF',     // Selected state background - COMMON
        selectedHover: '#DBEAFE', // Selected hover - COMMON
        disabled: '#F3F4F6',     // Disabled background
      },
      
      // Text colors
      text: {
        primary: '#111827',      // Main text - VERY COMMON
        secondary: '#6b7280',    // Secondary text - COMMON
        tertiary: '#9ca3af',     // Tertiary text - COMMON
        inverse: '#ffffff',      // Text on dark backgrounds
        disabled: '#9CA3AF',      // Disabled text - COMMON
        muted: '#6E6E6E',        // Muted text - COMMON
        dark: '#101018',         // Dark text variant - VERY COMMON
      },
      
      // Border colors
      border: {
        primary: '#D9D9DC',      // Primary border - MOST USED
        secondary: '#e5e7eb',    // Secondary border - COMMON
        tertiary: '#d1d5db',     // Tertiary border - COMMON
        focus: '#FB6F2A',        // Focus ring color - COMMON
        error: '#DC2626',        // Error border - COMMON
        info: '#2563EB',         // Info border - COMMON
      },
      
      // Interactive states
      interactive: {
        hover: '#f3f4f6',        // Hover background
        active: '#e5e7eb',        // Active background
        focus: 'rgba(251, 111, 42, 0.1)', // Focus ring overlay
        focusRing: '#FB6F2A',    // Focus ring color
      },
    },

    // ============================================
    // DARK THEME COLORS
    // Organized for easy theme switching
    // ============================================
    dark: {
      // Backgrounds
      background: {
        primary: '#111827',      // Main page background
        secondary: '#1f2937',    // Card/surface background
        tertiary: '#374151',     // Subtle background
        hover: '#1f2937',        // Hover state background
        selected: '#1e3a5f',     // Selected state background
        selectedHover: '#1e40af', // Selected hover
        disabled: '#374151',      // Disabled background
      },
      
      // Text colors
      text: {
        primary: '#f5f5f5',      // Main text
        secondary: '#e5e7eb',    // Secondary text
        tertiary: '#d1d5db',     // Tertiary text
        inverse: '#111827',      // Text on light backgrounds
        disabled: '#6b7280',     // Disabled text
        muted: '#9ca3af',        // Muted text
      },
      
      // Border colors
      border: {
        primary: '#374151',      // Primary border
        secondary: '#4b5563',    // Secondary border
        tertiary: '#6b7280',     // Tertiary border
        focus: '#FB6F2A',        // Focus ring color
        error: '#ef4444',        // Error border
        info: '#3b82f6',         // Info border
      },
      
      // Interactive states
      interactive: {
        hover: 'rgba(255, 255, 255, 0.1)', // Hover overlay
        active: 'rgba(255, 255, 255, 0.2)', // Active overlay
        focus: 'rgba(251, 111, 42, 0.2)',   // Focus ring overlay
        focusRing: '#FB6F2A',    // Focus ring color
      },
    },

    // ============================================
    // NAVIGATION COLORS (Navbar & Sidebar)
    // These are typically dark regardless of theme
    // ============================================
    navigation: {
      navbar: {
        background: '#2D2D2D',   // Dark navbar background (was rgb(35, 35, 35))
        text: '#ffffff',         // Navbar text
        hover: 'rgba(255, 255, 255, 0.1)', // Navbar hover
      },
      sidebar: {
        background: '#1a1a1a',   // Dark sidebar background - COMMON
        backgroundAlt: '#232323', // Alternative dark background
        text: 'rgba(255, 255, 255, 0.8)', // Sidebar text
        textActive: '#FB6F2A',   // Active sidebar text - COMMON
        hover: 'rgba(255, 255, 255, 0.1)', // Sidebar hover
        active: 'linear-gradient(270deg, #FB6F2A 0%, #FF9A66 100%)', // Active gradient
        activeHover: 'linear-gradient(270deg, rgba(251, 111, 42, 0.45) 0%, rgba(255, 154, 102, 0.45) 100%)',
        border: 'rgba(255, 255, 255, 0.1)', // Sidebar border
      },
    },

    // ============================================
    // LEGACY/COMPATIBILITY COLORS
    // (Maintained for backward compatibility)
    // These map to light theme for backward compatibility
    // ============================================
    background: '#f5f5f5',
    accent: '#FB6F2A',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    text: {
      primary: '#111827',
      secondary: '#6b7280',
      tertiary: '#9ca3af',
      inverse: '#ffffff',
    },
  },

  // ============================================
  // SPACING
  // ============================================
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
  },

  // ============================================
  // BORDER RADIUS
  // ============================================
  borderRadius: {
    sm: '0.25rem',   // 4px
    md: '0.375rem',  // 6px
    lg: '0.5rem',    // 8px (default)
    xl: '0.75rem',   // 12px
    '2xl': '1rem',   // 16px
    full: '9999px',  // Fully rounded
  },

  // ============================================
  // TYPOGRAPHY
  // ============================================
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },

  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Duplicate for compatibility (some components use fontWeight vs fontWeights)
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
  },

  lineHeight: {
    none: 1,
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },

  fonts: {
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'Menlo, Monaco, "Courier New", monospace',
  },

  // ============================================
  // SHADOWS
  // ============================================
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    // Orange glow shadows
    accent: '0 2px 4px rgba(251, 111, 42, 0.2)',
    accentHover: '0 4px 12px rgba(251, 111, 42, 0.3)',
    accentFocus: '0 0 0 3px rgba(251, 111, 42, 0.1)',
    // Dropdown shadows
    dropdown: '0 4px 12px rgba(0, 0, 0, 0.1)',
    dropdownDark: '0 4px 12px rgba(0, 0, 0, 0.3)',
  },

  // ============================================
  // BREAKPOINTS
  // ============================================
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  // ============================================
  // Z-INDEX LAYERS
  // ============================================
  zIndices: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  },

  // ============================================
  // AVATAR SIZES
  // ============================================
  avatar: {
    sizes: {
      xs: '20px',
      sm: '24px',
      md: '30px',
      lg: '50px',
      xl: '64px',
    },
  },
} as const;

export type Theme = typeof theme;
export type DynamicTheme = ReturnType<typeof createTheme>;

/**
 * Creates a dynamic theme object based on the current mode.
 * This allows components to access theme.colors.background.primary directly
 * without checking if theme is light or dark.
 * 
 * @param mode - 'light' or 'dark'
 * @returns Complete theme object with current mode's colors at root level
 * 
 * @example
 * ```tsx
 * const dynamicTheme = createTheme('dark');
 * // Now components can use:
 * // background: ${({ theme }) => theme.colors.background.primary};
 * // This will automatically be dark theme's background.primary
 * ```
 */
export const createTheme = (mode: 'light' | 'dark' = 'light') => {
  // Get colors for current mode
  const modeColors = theme.colors[mode];

  // Base theme (mode-agnostic properties)
  const baseTheme = {
    spacing: theme.spacing,
    borderRadius: theme.borderRadius,
    fontSizes: theme.fontSizes,
    fontWeights: theme.fontWeights,
    fontWeight: theme.fontWeight,
    fontSize: theme.fontSize,
    lineHeight: theme.lineHeight,
    fonts: theme.fonts,
    shadows: theme.shadows,
    breakpoints: theme.breakpoints,
    zIndices: theme.zIndices,
    avatar: theme.avatar,
  };

  return {
    ...baseTheme,
    mode, // Include current mode in theme
    colors: {
      // Current mode's colors at root level (automatic switching)
      background: modeColors.background,
      text: modeColors.text,
      border: {
        ...theme.colors.border, // Base border colors (theme-agnostic)
        ...modeColors.border,   // Mode-specific borders override
      },
      interactive: modeColors.interactive,
      
      // Theme-agnostic colors (always available)
      primary: theme.colors.primary,
      gray: theme.colors.gray,
      status: theme.colors.status,
      button: theme.colors.button,
      overlay: theme.colors.overlay,
      special: theme.colors.special,
      chart: theme.colors.chart,
      gradients: theme.colors.gradients,
      white: theme.colors.white,
      black: theme.colors.black,
      navigation: theme.colors.navigation,
      
      // Legacy compatibility (map to current mode or static values)
      accent: theme.colors.accent,
      success: theme.colors.success,
      warning: theme.colors.warning,
      error: theme.colors.error,
    },
  };
};

/**
 * Helper function to get theme-aware colors
 * Usage: getThemeColors('light') or getThemeColors('dark')
 * 
 * @param themeMode - 'light' or 'dark'
 * @returns Theme color object for the specified mode
 * 
 * @example
 * ```tsx
 * const colors = getThemeColors('light');
 * background: colors.background.primary;
 * color: colors.text.primary;
 * ```
 */
export const getThemeColors = (themeMode: 'light' | 'dark' = 'light') => {
  return themeMode === 'light' ? theme.colors.light : theme.colors.dark;
};

/**
 * Helper function to get current theme mode from context or localStorage
 * This can be extended to work with a theme context provider
 * 
 * @returns 'light' or 'dark'
 */
export const getCurrentThemeMode = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('theme-mode');
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  }
  return 'light'; // Default to light theme
};

/**
 * Helper function to toggle theme mode
 * Saves preference to localStorage
 * 
 * @param currentMode - Current theme mode
 * @returns Opposite theme mode
 */
export const toggleThemeMode = (currentMode: 'light' | 'dark'): 'light' | 'dark' => {
  const newMode = currentMode === 'light' ? 'dark' : 'light';
  if (typeof window !== 'undefined') {
    localStorage.setItem('theme-mode', newMode);
  }
  return newMode;
};

export default theme;
